import { useMemo, useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Search, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import type { Project } from "@shared/schema";

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE");
}

export default function CompletedProjectsPage() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: projects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  const completedProjects = useMemo(() => {
    if (!projects) return [];
    const sorted = projects.filter((p) => p.status === "completed");
    sorted.sort((a, b) => {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => {
      const fields = [p.name, p.customer ?? "", p.address ?? ""].map((s) =>
        s.toLowerCase(),
      );
      return fields.some((f) => f.includes(q));
    });
  }, [projects, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-2">
        <Link href="/dashboard">
          <Button variant="ghost" size="sm" className="text-gray-500 hover:text-bauwerk-teal -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </Link>
      </div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-bauwerk-teal mb-2">
          Abgeschlossene Angebote
        </h1>
        <p className="text-gray-600">
          Alle Projekte, die als abgeschlossen markiert wurden – sortiert nach
          Datum (neueste zuerst).
        </p>
      </div>

      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="relative max-w-xl">
            <Input
              id="completed-search"
              placeholder="Nach Projektname, Kunde oder Adresse suchen…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 focus:ring-bauwerk-teal focus:border-bauwerk-teal"
              data-testid="input-search-completed"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal flex items-center justify-between">
            <span>Abgeschlossene Angebote</span>
            {!isLoading && (
              <span className="text-sm font-normal text-gray-500">
                {completedProjects.length}{" "}
                {completedProjects.length === 1 ? "Eintrag" : "Einträge"}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="flex justify-between items-center p-4 border rounded-lg"
                >
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-24" />
                </div>
              ))}
            </div>
          ) : completedProjects.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-1">
                {searchQuery
                  ? "Keine abgeschlossenen Angebote zu diesem Suchbegriff."
                  : "Es gibt noch keine abgeschlossenen Angebote."}
              </p>
              {!searchQuery && (
                <p className="text-sm text-gray-500">
                  Sobald ein Projekt auf „Abgeschlossen" gesetzt wird, erscheint
                  es hier.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {completedProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex justify-between items-center p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/project/edit/${project.id}`)}
                  data-testid={`completed-project-row-${project.id}`}
                >
                  <div className="flex-1">
                    <h4 className="font-semibold text-bauwerk-teal mb-1">
                      {project.name}
                    </h4>
                    {project.customer && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Kunde:</span>{" "}
                        {project.customer}
                      </p>
                    )}
                    {project.address && (
                      <p className="text-sm text-gray-600">{project.address}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Erstellt am {formatDate(project.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-bauwerk-teal text-bauwerk-teal hover:bg-bauwerk-mint shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/project/${project.id}/protokoll`);
                      }}
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      Protokoll
                    </Button>
                    <Badge className="bg-green-100 text-green-800">
                      Abgeschlossen
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
